import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

/**
 * Fallback page for `/auth/verify-magic-link/:token`.
 *
 * When a user taps a magic link and iOS/Android universal/app links are
 * working, the OS intercepts the URL and opens konnect-mobile directly —
 * this page is never rendered.
 *
 * This page handles the case where the app is not installed or universal
 * links fail (e.g. email client WebViews). It attempts a custom-scheme
 * redirect first, then shows a manual "Open in App" button and download
 * guidance.
 */
export default function MagicLinkRedirectPage() {
  const { token } = useParams<{ token: string }>();
  const [showFallback, setShowFallback] = useState(false);

  const customSchemeUrl = `konnectbetter://verify-magic-link/${token ?? ''}`;

  useEffect(() => {
    if (!token) return;

    // Attempt to open the app via custom scheme. If the app is installed,
    // the browser will navigate away. If not, nothing visible happens and
    // the timeout below reveals the fallback UI.
    window.location.href = customSchemeUrl;

    const timer = setTimeout(() => setShowFallback(true), 1500);
    return () => clearTimeout(timer);
  }, [token, customSchemeUrl]);

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <h1 className="text-3xl font-bold text-primary dark:text-theme-primary mb-4">
            Invalid Link
          </h1>
          <p className="text-lg text-muted">
            This sign-in link appears to be invalid. Please request a new one
            from the app.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md mx-auto"
      >
        {!showFallback ? (
          <>
            <div className="mb-6">
              <div className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-primary dark:text-theme-primary mb-2">
              Opening konnect better...
            </h1>
            <p className="text-theme-secondary">
              You should be redirected to the app shortly.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-primary dark:text-theme-primary mb-4">
              Open in the App
            </h1>
            <p className="text-theme-secondary mb-8">
              Tap the button below to sign in with the konnect better app.
            </p>

            <a href={customSchemeUrl} className="btn-primary mb-6">
              Open konnect better
            </a>

            <p className="text-sm text-muted">
              Don&apos;t have the app yet? Contact your administrator for
              access.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
